<script setup>
import { useForm, router } from '@inertiajs/vue3';
import { computed, getCurrentInstance, ref } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Password from 'primevue/password';
import Select from 'primevue/select';
import Card from 'primevue/card';
import Message from 'primevue/message';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'primevue/usetoast';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    exchangeData: Object,
});

const form = useForm({
    first_name: '',
    last_name: '',
    name: '',
    birthday: '',
    phone: '',
    email: '',
    password: '',
    locale: 'en',
    coin: 'USD',
    full: true,
    notify_updates_via_email: 'MINIMAL',
});

const showPassword = ref(false);

const localeOptions = computed( () => [
    { name: 'English', code: 'en' },
    { name: 'Espanhol', code: 'es' },
    { name: 'Português', code: 'pt' }
]);

const coinOptions = computed( () =>[
    { name: 'USD', code: 'USD' },
    { name: 'EUR', code: 'EUR' },
    { name: 'BRL', code: 'BRL' },
    { name: 'GBP', code: 'GBP' }
]);

const dynamicCurrencyOptions = computed(() => {
    if (!props.exchangeData) {
        return [
            { label: 'BRL', value: 'BRL' },
            { label: 'EUR', value: 'EUR' },
            { label: 'USD', value: 'USD' },
            { label: 'GBP', value: 'GBP' },
        ];
    }
    
    return props.exchangeData.map(exchange => ({
        label: exchange.currency,
        value: exchange.currency
    }));
});

const emailNotificationOptions = computed( () => [
    { label: $t('Disabled'), value: 'DISABLED' },
    { label: $t('Minimal'), value: 'MINIMAL' },
    { label: $t('All'), value: 'ALL' },
]);

const generatePassword = () => {
    form.password = Math.random().toString(36).slice(-8);
};

const translateError = (error) => {
    if (!error) return error;
    if (error.startsWith('name_duplicate|')) {
        const name = error.slice('name_duplicate|'.length);
        return $t('A client with the name ":name" is already registered.').replace(':name', name);
    }
    return error;
};

const submit = () => {
    form.post(route('clients.store'), {
        onStart: () => showLoading('Creating client...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Client created successfully',
                life: 3000
            });
        },
        onError: (errors) => {
            const firstError = translateError(Object.values(errors)[0]);
            toast.add({
                severity: 'error',
                summary: $t('Error'),
                detail: firstError || $t('Failed to create client'),
                life: 6000
            });
        }
    });
};
</script>

<template>
    <AdminMainLayout>
        <div class="grid gap-6">
            <div class="col-12 lg:col-8">
                <Card>
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-user-plus text-2xl"></i>
                            <span>{{ $t('Add Client') }}</span>
                        </div>
                    </template>

                    <template #content>
                        <form @submit.prevent="submit">
                            <div class="formgrid grid">
                                <!-- First Name -->
                                <div class="field col-12 md:col-6">
                                    <label for="first_name" class="font-semibold">{{ $t('First name') }} <span class="text-red-500">*</span></label>
                                    <InputText
                                        id="first_name"
                                        v-model="form.first_name"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.first_name }"
                                        :placeholder="$t('Enter first name')"
                                        autocomplete="given-name"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.first_name" class="p-error">{{ translateError(form.errors.first_name) }}</small>
                                </div>

                                <!-- Last Name -->
                                <div class="field col-12 md:col-6">
                                    <label for="last_name" class="font-semibold">{{ $t('Last name') }} <span class="text-red-500">*</span></label>
                                    <InputText
                                        id="last_name"
                                        v-model="form.last_name"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.last_name }"
                                        :placeholder="$t('Enter last name')"
                                        autocomplete="family-name"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.last_name" class="p-error">{{ form.errors.last_name }}</small>
                                </div>

                                <!-- Username -->
<!--                                <div class="field col-12 md:col-6">-->
<!--                                    <label for="name" class="font-semibold">{{ $t('Username') }}</label>-->
<!--                                    <InputText-->
<!--                                        id="name"-->
<!--                                        v-model="form.name"-->
<!--                                        class="w-full"-->
<!--                                        :class="{ 'p-invalid': form.errors.name }"-->
<!--                                        :placeholder="$t('Enter username (optional)')"-->
<!--                                        autocomplete="username"-->
<!--                                        :disabled="form.processing" />-->
<!--                                    <small v-if="form.errors.name" class="p-error">{{ form.errors.name }}</small>-->
<!--                                </div>-->

                                <!-- Birthday -->
                                <div class="field col-12 md:col-6">
                                    <label for="birthday" class="font-semibold">{{ $t('Birthday') }}</label>
                                    <InputText
                                        id="birthday"
                                        v-model="form.birthday"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.birthday }"
                                        :placeholder="$t('Enter birthday')"
                                        autocomplete="bday"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.birthday" class="p-error">{{ form.errors.birthday }}</small>
                                </div>

                                <!-- Email -->
                                <div class="field col-12 md:col-6">
                                    <label for="email" class="font-semibold">{{ $t('Email') }} <span class="text-red-500">*</span></label>
                                    <InputText
                                        id="email"
                                        v-model="form.email"
                                        type="email"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.email }"
                                        :placeholder="$t('Enter email address')"
                                        autocomplete="email"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.email" class="p-error">{{ form.errors.email }}</small>
                                </div>

                                <!-- Phone -->
                                <div class="field col-12 md:col-6">
                                    <label for="phone" class="font-semibold">{{ $t('Phone') }}</label>
                                    <InputText
                                        id="phone"
                                        v-model="form.phone"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.phone }"
                                        :placeholder="$t('Enter phone number')"
                                        autocomplete="tel"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.phone" class="p-error">{{ form.errors.phone }}</small>
                                </div>

                                <!-- Password -->
                                <div class="field col-12">
                                    <label for="password" class="font-semibold">{{ $t('Password') }} <span class="text-red-500">*</span></label>
                                    <div class="flex gap-2">
                                        <Password
                                            id="password"
                                            v-model="form.password"
                                            class="flex-1"
                                            :inputClass="{ 'p-invalid': form.errors.password }"
                                            :placeholder="$t('Enter password')"
                                            :feedback="true"
                                            :toggleMask="true"
                                            autocomplete="new-password"
                                            :disabled="form.processing" />
                                        <Button
                                            type="button"
                                            :label="$t('Generate')"
                                            icon="pi pi-refresh"
                                            class="p-button-secondary"
                                            @click="generatePassword"
                                            :disabled="form.processing" />
                                    </div>
                                    <small v-if="form.errors.password" class="p-error">{{ form.errors.password }}</small>
                                </div>

                                <!-- Locale -->
                                <div class="field col-12 md:col-6">
                                    <label for="locale" class="font-semibold">{{ $t('Locale') }}</label>
                                    <Select
                                        id="locale"
                                        v-model="form.locale"
                                        :options="localeOptions"
                                        optionLabel="name"
                                        optionValue="code"
                                        :placeholder="$t('Select Locale')"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.locale }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.locale" class="p-error">{{ form.errors.locale }}</small>
                                </div>

                                <!-- Coin -->
                                <div class="field col-12 md:col-6">
                                    <label for="coin" class="font-semibold">{{ $t('Coin') }}</label>
                                    <Select
                                        id="coin"
                                        v-model="form.coin"
                                        :options="dynamicCurrencyOptions"
                                        optionLabel="label"
                                        optionValue="value"
                                        :placeholder="$t('Select Coin')"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.coin }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.coin" class="p-error">{{ form.errors.coin }}</small>
                                </div>

                                <!-- E-mail notifications -->
                                <div class="field col-12 md:col-6">
                                    <label for="notifications" class="font-semibold">{{ $t('E-mail notifications') }}</label>
                                    <Select
                                        id="notifications"
                                        v-model="form.notify_updates_via_email"
                                        :options="emailNotificationOptions"
                                        optionLabel="label"
                                        optionValue="value"
                                        :placeholder="$t('Select notification frequency')"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.notify_updates_via_email }"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.notify_updates_via_email" class="p-error">{{ form.errors.notify_updates_via_email }}</small>
                                </div>

<!--                                &lt;!&ndash; Full Access &ndash;&gt;-->
<!--                                <div class="field col-12">-->
<!--                                    <div class="flex align-items-center">-->
<!--                                        <Checkbox-->
<!--                                            id="full"-->
<!--                                            v-model="form.full"-->
<!--                                            :binary="true"-->
<!--                                            :disabled="form.processing" />-->
<!--                                        <label for="full" class="ml-2 font-semibold">{{ $t('Full Access') }}</label>-->
<!--                                    </div>-->
<!--                                    <small v-if="form.errors.full" class="p-error">{{ form.errors.full }}</small>-->
<!--                                </div>-->
                            </div>

                            <div class="flex gap-2 mt-4">
                                <Button
                                    type="submit"
                                    :label="$t('Save')"
                                    icon="pi pi-check"
                                    :loading="form.processing"
                                    :disabled="form.processing" />

                                <Button
                                    :label="$t('Cancel')"
                                    icon="pi pi-times"
                                    class="p-button-secondary"
                                    @click="router.visit(route('clients.index'), {
                                        onStart: () => showLoading('Loading...'),
                                        onFinish: () => hideLoading()
                                    })"
                                    :disabled="form.processing" />
                            </div>

                            <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                                {{ $t('Client saved successfully.') }}
                            </Message>
                        </form>
                    </template>
                </Card>
            </div>

<!--            <div class="col-12 lg:col-4">-->
<!--                <Card>-->
<!--                    <template #title>-->
<!--                        <div class="flex align-items-center gap-2">-->
<!--                            <i class="pi pi-info-circle"></i>-->
<!--                            <span>{{ $t('Information') }}</span>-->
<!--                        </div>-->
<!--                    </template>-->
<!--&lt;!&ndash;                    <template #content>&ndash;&gt;-->
<!--&lt;!&ndash;                        <p class="m-0">&ndash;&gt;-->
<!--&lt;!&ndash;                            {{ $t('Create a new client account with access to the platform. Fill out the required fields to set up their profile and permissions.') }}&ndash;&gt;-->
<!--&lt;!&ndash;                        </p>&ndash;&gt;-->
<!--&lt;!&ndash;                        <div class="mt-3">&ndash;&gt;-->
<!--&lt;!&ndash;                            <small class="text-gray-600">&ndash;&gt;-->
<!--&lt;!&ndash;                                <strong>{{ $t('Required fields:') }}</strong>&ndash;&gt;-->
<!--&lt;!&ndash;                                <ul class="mt-2 pl-4">&ndash;&gt;-->
<!--&lt;!&ndash;                                    <li>{{ $t('First and last name') }}</li>&ndash;&gt;-->
<!--&lt;!&ndash;                                    <li>{{ $t('Email address') }}</li>&ndash;&gt;-->
<!--&lt;!&ndash;                                    <li>{{ $t('Password') }}</li>&ndash;&gt;-->
<!--&lt;!&ndash;                                </ul>&ndash;&gt;-->
<!--&lt;!&ndash;                            </small>&ndash;&gt;-->
<!--&lt;!&ndash;                        </div>&ndash;&gt;-->
<!--&lt;!&ndash;                        <div class="mt-3">&ndash;&gt;-->
<!--&lt;!&ndash;                            <small class="text-gray-600">&ndash;&gt;-->
<!--&lt;!&ndash;                                <strong>{{ $t('Full Access:') }}</strong>&ndash;&gt;-->
<!--&lt;!&ndash;                                <p class="mt-1">{{ $t('Enable this option to give the client full access to all platform features.') }}</p>&ndash;&gt;-->
<!--&lt;!&ndash;                            </small>&ndash;&gt;-->
<!--&lt;!&ndash;                        </div>&ndash;&gt;-->
<!--&lt;!&ndash;                    </template>&ndash;&gt;-->
<!--                </Card>-->
<!--            </div>-->
        </div>
    </AdminMainLayout>
</template>

<style scoped>

:deep(.p-select) {
    height: 42px;
}

:deep(.p-select .p-select-label) {
    padding: 8px 12px;
    line-height: 1.5;
}
.field {
    margin-bottom: 1.5rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}

.p-error {
    color: var(--red-500);
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
}
</style>
